import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {get} from "lit-translate";
import {DateTime} from "luxon";

import {AbstractDialogComponent} from "../ha-dialog/abstract-dialog.component";
import type {ReleaseNotesEntry} from "../../helpers/interfaces/release-notes-entry.interface";
import type {ReleaseNotesSection} from "../../helpers/interfaces/release-notes-section.interface";
import {RELEASE_NOTES} from "../../release-notes.data";

/**
 * "Versionshinweise" dialog, opened from the settings menu: lists every released version of
 * frontend/RELEASE-NOTES.md with its sections, and marks the first one as the current version.
 *
 * The dialog renders the entries in the order it receives them and does not sort; the file guard
 * test makes sure the file itself is ordered newest first. Items are bound as text, never as HTML.
 */
@customElement('ha-release-notes-dialog')
export class HaReleaseNotesDialog extends AbstractDialogComponent {

    /** The entries to show; defaults to the bundled file, a test passes its own. */
    @property({attribute: false})
    public entries: readonly ReleaseNotesEntry[] = RELEASE_NOTES;

    public constructor() {
        super();
        this.closable = true;
    }

    /** The dialog title is rendered reactively so it re-translates on language change. */
    protected _renderHeader(): unknown {
        return get('releaseNotes.title');
    }

    protected _renderMainContent(): unknown {
        if (this.entries.length === 0) {
            // In a shipped bundle this only happens when the loader caught a parse error.
            return html`<p class="release-notes__empty">${get('releaseNotes.empty')}</p>`;
        }

        return html`
            <div class="release-notes__list">
                ${this.entries.map((entry, index) => this._renderEntry(entry, index === 0))}
            </div>
        `;
    }

    /** One version: heading with number, date and the "current" marker, then its sections. */
    private _renderEntry(entry: ReleaseNotesEntry, isCurrent: boolean): unknown {
        return html`
            <section class="release-notes__entry">
                <h3 class="release-notes__heading">
                    <span class="release-notes__version">${entry.version}</span>
                    <span class="release-notes__date">${this._formatDate(entry.date)}</span>
                    ${isCurrent ? html`<span class="release-notes__current">${get('releaseNotes.current')}</span>` : null}
                </h3>
                ${entry.sections.map(section => this._renderSection(section))}
            </section>
        `;
    }

    /** One category with its items as a list. */
    private _renderSection(section: ReleaseNotesSection): unknown {
        return html`
            <h4 class="release-notes__category">${section.category}</h4>
            <ul class="release-notes__items">
                ${section.items.map(item => html`<li>${item}</li>`)}
            </ul>
        `;
    }

    /** ISO date to the German short form, e.g. 2026-10-07 to "7. Okt. 2026". */
    private _formatDate(isoDate: string): string {
        return DateTime.fromISO(isoDate).setLocale('de').toLocaleString(DateTime.DATE_MED);
    }
}
